/* eslint-disable jsx-a11y/iframe-has-title */
import { useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import { FaEdit, FaTrash } from "react-icons/fa";
import { ROUTES } from "../App";
import { database } from "../constants/firebase";
import { useLocation, useParams } from "react-router";
import DeleteModal from "../components/DeleteModal";
import MobileHeader from "../components/MobileHeader";
import BunnyVideoEmbed from "../components/BunnyVideoEmbed";
import { LuLink } from "react-icons/lu";
import { onValue, ref, remove } from "firebase/database";
import { deleteBunnyCollections } from "../utils/utils";

const ChapterContent = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { courseId } = useParams();
    const location = useLocation();
    const chapterId = location.state?.chapterId;

    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedLessonId, setSelectedLessonId] = useState(null);
    const [loadedImages, setLoadedImages] = useState({}); // track image loading
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [openLinkDropdown, setOpenLinkDropdown] = useState(null);

    useEffect(() => {
        if (!currentUser || !courseId || !chapterId) return;

        const lessonsRef = ref(database, `Listings/${courseId}/chapters/${chapterId}/lessons`);
        const unsubscribe = onValue(lessonsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setLessons(Object.entries(data).map(([id, val]) => ({ id, ...val })));
            } else {
                setLessons([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, courseId, chapterId]);

    const handleDelete = async () => {
        if (!selectedLessonId) return;

        try {
            setIsDeleting(true);

            // 1️⃣ GET current lesson to extract uid (if needed for collection name)
            const lessonToDelete = lessons.find(l => l.id === selectedLessonId);

            // 2️⃣ DELETE bunny collection (lessons_uid + showreels_uid)
            if (lessonToDelete?.id) {
                await deleteBunnyCollections(lessonToDelete.id);
            }

            // 3️⃣ DELETE lesson from Firebase
            await remove(
                ref(database, `Listings/${courseId}/chapters/${chapterId}/lessons/${selectedLessonId}`)
            );

            window.showToast("Lesson deleted successfully", "success");
        } catch (err) {
            console.error("Delete failed:", err);
            window.showToast("Failed to delete lesson", "error");
        } finally {
            setIsDeleting(false);
            setIsDeleteOpen(false);
            setSelectedLessonId(null);
        }
    };


    const handleImageLoad = (id) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    const LessonSkeleton = () => (
        <div className="space-y-4 w-full">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center justify-between border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4 animate-pulse rounded-lg">
                    <div className="flex gap-4 md:max-w-[40%] max-w-full">
                        <div className="w-16 h-16 md:w-24 md:h-24 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-20"></div>
                            <div className="h-3 bg-gray-300 dark:bg-gray-700 w-40"></div>
                        </div>
                    </div>
                    <div className="w-20 h-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title="Chapter Content" isBackBtn={true} />

            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto relative">
                    {/* Top Header */}
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Chapter Content</h2>
                        <div />
                    </div>

                    {/* Lessons List */}
                    <div className="flex-1 overflow-y-auto px-2 md:py-8 py-3 flex md:mt-16 mt-1">
                        <div className="w-full flex flex-col justify-center items-center min-h-full">
                            {loading ? (
                                <LessonSkeleton />
                            ) : lessons.length === 0 ? (
                                <p className="text-center text-gray-500 dark:text-gray-400">
                                    No lessons found.
                                </p>
                            ) : (
                                <div className="w-full space-y-4">
                                    {lessons.map((lesson) => {
                                        const isLoaded = loadedImages[lesson.id];
                                        return (
                                            <div
                                                key={lesson.id}
                                                className="flex items-center justify-between border-t text-black dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white hover:bg-gray-100 dark:hover:bg-[#2A2A2A] px-4 py-4 hover:rounded-lg"
                                            >
                                                <div
                                                    className="flex gap-4 cursor-pointer md:max-w-[40%] max-w-full"
                                                    onClick={() => {
                                                        setIsIframeLoading(true);
                                                        setSelectedLesson(lesson);
                                                    }}
                                                >
                                                    {/* Image with shimmer */}
                                                    <div className="relative w-16 h-16 md:w-24 md:h-24 flex-shrink-0">
                                                        {!isLoaded && (
                                                            <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                                                        )}
                                                        <img
                                                            src={lesson.coverImageUrl || IMAGES.courses}
                                                            alt="Lesson Cover"
                                                            className={`w-16 h-16 md:w-24 md:h-24 rounded-lg object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
                                                            onLoad={() => handleImageLoad(lesson.id)}
                                                            onError={() => handleImageLoad(lesson.id)}
                                                        />
                                                    </div>

                                                    <div>
                                                        <div className="font-bold text-lg line-clamp-1">{lesson.title}</div>
                                                        <div className="text-sm text-gray-700 dark:text-white line-clamp-3 min-w-full">
                                                            {lesson.description || "No description provided"}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Buttons & Links */}
                                                <div className="items-center space-y-3">
                                                    <div className="flex items-center md:gap-4 gap-1 text-sm" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            className="flex items-center gap-1 text-green-500 bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30"
                                                            onClick={() =>
                                                                navigate(ROUTES.ADD_LESSON, { state: { lesson, courseId, chapterId } })
                                                            }
                                                        >
                                                            <FaEdit /> Edit
                                                        </button>
                                                        <span className="text-gray-400">|</span>
                                                        <button
                                                            className="flex items-center gap-1 text-red-500 bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30"
                                                            onClick={() => {
                                                                setSelectedLessonId(lesson.id);
                                                                setIsDeleteOpen(true);
                                                            }}
                                                        >
                                                            <FaTrash /> Delete
                                                        </button>
                                                    </div>

                                                    {lesson.courseLinks?.length > 0 && (
                                                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                                                            {/* Dropdown Button */}
                                                            <button
                                                                onClick={() =>
                                                                    setOpenLinkDropdown(
                                                                        openLinkDropdown === lesson.id ? null : lesson.id
                                                                    )
                                                                }
                                                                className="flex w-28 items-center justify-between gap-1 text-primary bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md"
                                                            >
                                                                <span className="flex items-center gap-1">
                                                                    <LuLink /> Links
                                                                </span>
                                                                <span className="text-xs">▼</span>
                                                            </button>

                                                            {/* Dropdown Menu */}
                                                            {openLinkDropdown === lesson.id && (
                                                                <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg" style={{ zIndex: 999 }}>
                                                                    {lesson.courseLinks.map((link, index) => (
                                                                        <a
                                                                            key={index}
                                                                            href={link}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-[#2A2A2A]"
                                                                        >
                                                                            Link {index + 1}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add Lesson Button */}
                    <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-3xl">
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.ADD_LESSON, { state: { courseId, chapterId } })}
                            className="w-full bg-[#327691] hover:bg-[#2a647a] transition-colors text-white font-semibold rounded-2xl md:py-5 py-3 text-lg md:text-xl"
                        >
                            Add Lesson
                        </button>
                    </div>

                    {/* Video/PDF Modal */}
                    {selectedLesson && (
                        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-[#1A1A1A] rounded-xl shadow-xl p-6 w-[90%] md:w-[70%] lg:w-[50%] relative">
                                {/* Close Button */}
                                <button
                                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                                    onClick={() => setSelectedLesson(null)}
                                >
                                    ✖
                                </button>

                                <h3 className="text-xl font-semibold mb-4">{selectedLesson.title}</h3>

                                {/* PDF */}
                                {selectedLesson.fileUrl && selectedLesson.fileUrl.endsWith(".pdf") ? (
                                    <iframe
                                        src={selectedLesson.fileUrl}
                                        title="PDF Viewer"
                                        className="w-full h-[450px] rounded-lg border-0"
                                    ></iframe>
                                ) : (
                                    // Bunny.net / other video iframe
                                    <BunnyVideoEmbed
                                        videoUrl={selectedLesson.fileUrl}
                                        orientation={selectedLesson.videoOrientation}
                                        videoWidth={selectedLesson.videoWidth}
                                        videoHeight={selectedLesson.videoHeight}
                                        variant="modal"
                                        isLoading={isIframeLoading}
                                        onLoad={() => setIsIframeLoading(false)}
                                        wrapperClassName="mx-auto shadow-xl"
                                        iframeClassName={isIframeLoading ? "opacity-0 transition-opacity duration-300" : "opacity-100 transition-opacity duration-300"}
                                        title={selectedLesson.title || "Lesson video"}
                                    />
                                )}
                            </div>
                        </div>
                    )}


                    <DeleteModal
                        isOpen={isDeleteOpen}
                        onClose={() => setIsDeleteOpen(false)}
                        onConfirm={handleDelete}
                        isDeleting={isDeleting}
                    />
                </main>
            </div>
        </div>
    );
};

export default ChapterContent;
